import { getColor, getBackgroundColor, getOutlineColor, } from '../../colors/colors.alias';
import { getBorderRadius, getOutlineOffset, getOutlineStyle, getOutlineWidth, } from '../../borders/borders.alias';
import { getWidth, getMinHeight } from '../../sizes/sizes.alias';
import { getOpacity } from '../../opacity/opacity.alias';
import getIcons from '../../icons/icons.alias';
import { constants as Paragraph } from '../Paragraph';
import { constants as Heading } from '../Heading';
import { constants as Icon } from '../Icon';
import { VARIANT_ERROR, VARIANT_INFORMATIVE, VARIANT_SUCCESS, VARIANT_WARNING, ELEMENT_CONTAINER, ELEMENT_BAR, ELEMENT_ICON, ELEMENT_HEADING, ELEMENT_BODY_TEXT, ELEMENT_SHOW_ICON, INTERACTION_STATE_ACTIVE, INTERACTION_STATE_HOVER, INTERACTION_STATE_FOCUS_VISIBLE, } from '../../constants';
export const constants = Object.freeze({
    NAME: 'AttentionBlock',
    variants: {
        VARIANT_ERROR,
        VARIANT_INFORMATIVE,
        VARIANT_SUCCESS,
        VARIANT_WARNING,
    },
    elements: {
        ELEMENT_CONTAINER,
        ELEMENT_BAR,
        ELEMENT_ICON,
        ELEMENT_HEADING,
        ELEMENT_BODY_TEXT,
        ELEMENT_SHOW_ICON,
    },
    interactionStates: {
        INTERACTION_STATE_ACTIVE,
        INTERACTION_STATE_HOVER,
        INTERACTION_STATE_FOCUS_VISIBLE,
    },
});
export default function getAttentionBlock(transformer, config) {
    const backgroundColor = getBackgroundColor(transformer, config);
    const color = getColor(transformer, config);
    const outlineOffset = getOutlineOffset(transformer, config);
    const outlineWidth = getOutlineWidth(transformer, config);
    const outlineColor = getOutlineColor(transformer, config);
    const outlineStyle = getOutlineStyle(transformer, config);
    const borderRadius = getBorderRadius(transformer, config);
    const minHeight = getMinHeight(transformer);
    const opacity = getOpacity(transformer);
    const width = getWidth(transformer);
    const icons = getIcons();
    return Object.freeze({
        constants,
        elements: {
            [ELEMENT_CONTAINER]: {
                styles: {
                    color: color.text.default.value,
                    borderRadius: borderRadius.containerWithBackgroundColor.value,
                    minHeight: minHeight.attentionBlockContainer.value,
                },
                interactionStates: {
                    [INTERACTION_STATE_ACTIVE]: {
                        styles: {
                            opacity: opacity.attentionBlock.interactionStates[INTERACTION_STATE_ACTIVE].value,
                        },
                    },
                    [INTERACTION_STATE_HOVER]: {
                        styles: {
                            opacity: opacity.attentionBlock.interactionStates[INTERACTION_STATE_HOVER].value,
                        },
                    },
                    [INTERACTION_STATE_FOCUS_VISIBLE]: {
                        styles: {
                            outlineOffset: outlineOffset.default.value,
                            outlineStyle: outlineStyle.default.value,
                            outlineWidth: outlineWidth.default.value,
                            outlineColor: outlineColor.default.interactionStates[INTERACTION_STATE_FOCUS_VISIBLE].value,
                        },
                    },
                },
            },
            [ELEMENT_BAR]: {
                styles: {
                    width: width.attentionBlockBar.value,
                },
            },
            [ELEMENT_HEADING]: {
                components: {
                    [Heading.NAME]: {
                        variant: Heading.variants.VARIANT_SMALL,
                    },
                },
            },
            [ELEMENT_BODY_TEXT]: {
                components: {
                    [Paragraph.NAME]: {
                        variant: Paragraph.variants.VARIANT_MEDIUM,
                    },
                },
            },
            [ELEMENT_SHOW_ICON]: {
                components: {
                    [Icon.NAME]: {
                        variant: Icon.variants.VARIANT_SMALL,
                        name: icons.action.show.value,
                    },
                },
            },
        },
        variants: {
            [VARIANT_ERROR]: {
                elements: {
                    [ELEMENT_CONTAINER]: {
                        styles: {
                            backgroundColor: backgroundColor.feedback.negative.contentContainer.value,
                        },
                    },
                    [ELEMENT_BAR]: {
                        styles: {
                            backgroundColor: backgroundColor.feedback.negative.decorative.value,
                        },
                    },
                    [ELEMENT_ICON]: {
                        styles: {
                            color: color.feedback.negative.icon.value,
                        },
                        components: {
                            [Icon.NAME]: {
                                variant: Icon.variants.VARIANT_MEDIUM,
                                name: icons.feedback.negative.value,
                            },
                        },
                    },
                    [ELEMENT_SHOW_ICON]: {
                        styles: {
                            color: color.feedback.negative.icon.value,
                        },
                    },
                },
            },
            [VARIANT_INFORMATIVE]: {
                elements: {
                    [ELEMENT_CONTAINER]: {
                        styles: {
                            backgroundColor: backgroundColor.feedback.informative.contentContainer.value,
                        },
                    },
                    [ELEMENT_BAR]: {
                        styles: {
                            backgroundColor: backgroundColor.feedback.informative.decorative.value,
                        },
                    },
                    [ELEMENT_ICON]: {
                        styles: {
                            color: color.feedback.informative.icon.value,
                        },
                        components: {
                            [Icon.NAME]: {
                                variant: Icon.variants.VARIANT_MEDIUM,
                                name: icons.feedback.informative.value,
                            },
                        },
                    },
                    [ELEMENT_SHOW_ICON]: {
                        styles: {
                            color: color.feedback.informative.icon.value,
                        },
                    },
                },
            },
            [VARIANT_SUCCESS]: {
                elements: {
                    [ELEMENT_CONTAINER]: {
                        styles: {
                            backgroundColor: backgroundColor.feedback.positive.contentContainer.value,
                        },
                    },
                    [ELEMENT_BAR]: {
                        styles: {
                            backgroundColor: backgroundColor.feedback.positive.decorative.value,
                        },
                    },
                    [ELEMENT_ICON]: {
                        styles: {
                            color: color.feedback.positive.icon.value,
                        },
                        components: {
                            [Icon.NAME]: {
                                variant: Icon.variants.VARIANT_MEDIUM,
                                name: icons.feedback.positive.value,
                            },
                        },
                    },
                    [ELEMENT_SHOW_ICON]: {
                        styles: {
                            color: color.feedback.positive.icon.value,
                        },
                    },
                },
            },
            [VARIANT_WARNING]: {
                elements: {
                    [ELEMENT_CONTAINER]: {
                        styles: {
                            backgroundColor: backgroundColor.feedback.warning.contentContainer.value,
                        },
                    },
                    [ELEMENT_BAR]: {
                        styles: {
                            backgroundColor: backgroundColor.feedback.warning.decorative.value,
                        },
                    },
                    [ELEMENT_ICON]: {
                        styles: {
                            color: color.feedback.warning.icon.value,
                        },
                        components: {
                            [Icon.NAME]: {
                                variant: Icon.variants.VARIANT_MEDIUM,
                                name: icons.feedback.warning.value,
                            },
                        },
                    },
                    [ELEMENT_SHOW_ICON]: {
                        styles: {
                            color: color.feedback.warning.icon.value,
                        },
                    },
                },
            },
        },
    });
}
